import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useHttp } from "../hooks/useHttp";

interface ExpodemoClaim {
  emailAddress: string;
  claimedAt: string;
}

export const ExpoDemo = () => {
  const navigate = useNavigate();
  const { sendRequest } = useHttp();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [claim, setClaim] = useState<ExpodemoClaim | null>(null);
  const [isLoadingClaim, setIsLoadingClaim] = useState(false);

  const loadMyClaim = async () => {
    setIsLoadingClaim(true);
    try {
      const { data } = await sendRequest<{
        success: boolean;
        claim: ExpodemoClaim | null;
      }>({
        endpoint: "/expodemo/my-claim",
        method: "GET",
        responseType: "json",
      });

      if (data.success) {
        setClaim(data.claim);
      }
    } catch (err) {
      console.error("Failed to load expodemo claim:", err);
    } finally {
      setIsLoadingClaim(false);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data } = await sendRequest<{
          authenticated: boolean;
          email?: string;
        }>({
          endpoint: "/expodemo/session",
          method: "GET",
          responseType: "json",
        });

        setIsAuthenticated(data.authenticated);
        if (!data.authenticated) {
          setShowLoginModal(true);
        } else {
          loadMyClaim();
        }
      } catch (err) {
        setIsAuthenticated(false);
        setShowLoginModal(true);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async () => {
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { data } = await sendRequest<{
        success: boolean;
        message: string;
      }>({
        endpoint: "/expodemo/login",
        method: "POST",
        payload: { email },
        responseType: "json",
      });

      if (data.success) {
        setIsAuthenticated(true);
        setShowLoginModal(false);
        window.dispatchEvent(new CustomEvent("authStatusChanged"));
        loadMyClaim();
      } else {
        setError(data.message || "Failed to login");
      }
    } catch (err: any) {
      setError(err instanceof Error ? err.message : "Failed to login");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100dvh-149.5px)] p-6">
        <div className="flex justify-center items-center w-16 h-16 bg-blue-100 rounded-full animate-spin">
          <svg
            className="w-8 h-8 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </div>
      </div>
    );
  }

  if (showLoginModal && !isAuthenticated) {
    return (
      <div className="flex justify-center items-center bg-gray-50 min-h-[calc(100dvh-149.5px)] p-6">
        <div className="mx-auto max-w-md text-center">
          <div className="p-8 bg-white rounded-lg shadow-md">
            <img
              src="/images/223-logo.png"
              alt="223 Logo"
              className="mx-auto mb-4 max-w-[100px]"
            />

            <h1 className="mb-4 text-2xl font-bold text-gray-900">Login</h1>

            <p className="mb-8 text-gray-600">
              Enter your email to access your 223 portal.
            </p>

            <div className="mb-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@gmail.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleLogin();
                  }
                }}
              />
            </div>

            {error && (
              <div className="px-4 py-3 mb-4 text-sm text-red-700 bg-red-100 rounded border border-red-400">
                {error}
              </div>
            )}

            <button
              onClick={handleLogin}
              disabled={!email || isSubmitting}
              className="w-full py-3 font-bold text-white bg-[#1F1F1F] rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? "Logging in..." : "Login"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 space-y-5">
      <div className="border border-black rounded-[14px] p-3.5 w-full">
        <div className="flex justify-between items-center">
          <h6 className="font-bold leading-none">My 223 Portal</h6>

          <div className="flex items-center gap-1.5">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.3333 9.33333C11.5101 9.33333 11.6797 9.2631 11.8047 9.13807C11.9298 9.01305 12 8.84348 12 8.66667C12 8.48986 11.9298 8.32029 11.8047 8.19526C11.6797 8.07024 11.5101 8 11.3333 8C11.1565 8 10.987 8.07024 10.8619 8.19526C10.7369 8.32029 10.6667 8.48986 10.6667 8.66667C10.6667 8.84348 10.7369 9.01305 10.8619 9.13807C10.987 9.2631 11.1565 9.33333 11.3333 9.33333ZM11.3333 12C11.5101 12 11.6797 11.9298 11.8047 11.8047C11.9298 11.6797 12 11.5101 12 11.3333C12 11.1565 11.9298 10.987 11.8047 10.8619C11.6797 10.7369 11.5101 10.6667 11.3333 10.6667C11.1565 10.6667 10.987 10.7369 10.8619 10.8619C10.7369 10.987 10.6667 11.1565 10.6667 11.3333C10.6667 11.5101 10.7369 11.6797 10.8619 11.8047C10.987 11.9298 11.1565 12 11.3333 12ZM8.66667 8.66667C8.66667 8.84348 8.59643 9.01305 8.4714 9.13807C8.34638 9.2631 8.17681 9.33333 8 9.33333C7.82319 9.33333 7.65362 9.2631 7.5286 9.13807C7.40357 9.01305 7.33333 8.84348 7.33333 8.66667C7.33333 8.48986 7.40357 8.32029 7.5286 8.19526C7.65362 8.07024 7.82319 8 8 8C8.17681 8 8.34638 8.07024 8.4714 8.19526C8.59643 8.32029 8.66667 8.48986 8.66667 8.66667ZM8.66667 11.3333C8.66667 11.5101 8.59643 11.6797 8.4714 11.8047C8.34638 11.9298 8.17681 12 8 12C7.82319 12 7.65362 11.9298 7.5286 11.8047C7.40357 11.6797 7.33333 11.5101 7.33333 11.3333C7.33333 11.1565 7.40357 10.987 7.5286 10.8619C7.65362 10.7369 7.82319 10.6667 8 10.6667C8.17681 10.6667 8.34638 10.7369 8.4714 10.8619C8.59643 10.987 8.66667 11.1565 8.66667 11.3333ZM4.66667 9.33333C4.84348 9.33333 5.01305 9.2631 5.13807 9.13807C5.2631 9.01305 5.33333 8.84348 5.33333 8.66667C5.33333 8.48986 5.2631 8.32029 5.13807 8.19526C5.01305 8.07024 4.84348 8 4.66667 8C4.48986 8 4.32029 8.07024 4.19526 8.19526C4.07024 8.32029 4 8.48986 4 8.66667C4 8.84348 4.07024 9.01305 4.19526 9.13807C4.32029 9.2631 4.48986 9.33333 4.66667 9.33333ZM4.66667 12C4.84348 12 5.01305 11.9298 5.13807 11.8047C5.2631 11.6797 5.33333 11.5101 5.33333 11.3333C5.33333 11.1565 5.2631 10.987 5.13807 10.8619C5.01305 10.7369 4.84348 10.6667 4.66667 10.6667C4.48986 10.6667 4.32029 10.7369 4.19526 10.8619C4.07024 10.987 4 11.1565 4 11.3333C4 11.5101 4.07024 11.6797 4.19526 11.8047C4.32029 11.9298 4.48986 12 4.66667 12Z"
                fill="black"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4.66665 1.16667C4.79926 1.16667 4.92644 1.21935 5.0202 1.31312C5.11397 1.40689 5.16665 1.53406 5.16665 1.66667V2.17534C5.60798 2.16667 6.09398 2.16667 6.62865 2.16667H9.37065C9.90598 2.16667 10.392 2.16667 10.8333 2.17534V1.66667C10.8333 1.53406 10.886 1.40689 10.9798 1.31312C11.0735 1.21935 11.2007 1.16667 11.3333 1.16667C11.4659 1.16667 11.5931 1.21935 11.6869 1.31312C11.7806 1.40689 11.8333 1.53406 11.8333 1.66667V2.21801C12.0067 2.23134 12.1709 2.24823 12.326 2.26867C13.1073 2.37401 13.74 2.59534 14.2393 3.094C14.738 3.59334 14.9593 4.22601 15.0647 5.00734C15.1667 5.76734 15.1667 6.73734 15.1667 7.96267V9.37067C15.1667 10.596 15.1667 11.5667 15.0647 12.326C14.9593 13.1073 14.738 13.74 14.2393 14.2393C13.74 14.738 13.1073 14.9593 12.326 15.0647C11.566 15.1667 10.596 15.1667 9.37065 15.1667H6.62998C5.40465 15.1667 4.43398 15.1667 3.67465 15.0647C2.89332 14.9593 2.26065 14.738 1.76132 14.2393C1.26265 13.74 1.04132 13.1073 0.935984 12.326C0.833984 11.566 0.833984 10.596 0.833984 9.37067V7.96267C0.833984 6.73734 0.833984 5.76667 0.935984 5.00734C1.04132 4.22601 1.26265 3.59334 1.76132 3.094C2.26065 2.59534 2.89332 2.37401 3.67465 2.26867C3.83021 2.24823 3.99443 2.23134 4.16732 2.21801V1.66667C4.16732 1.53418 4.2199 1.4071 4.31353 1.31335C4.40715 1.2196 4.53416 1.16685 4.66665 1.16667ZM3.80665 3.26C3.13665 3.35001 2.74998 3.51934 2.46798 3.80134C2.18598 4.08334 2.01665 4.47001 1.92665 5.14001C1.91154 5.25334 1.89865 5.37312 1.88798 5.49934H14.112C14.1013 5.37312 14.0884 5.25312 14.0733 5.13934C13.9833 4.46934 13.814 4.08267 13.532 3.80067C13.25 3.51867 12.8633 3.34934 12.1927 3.25934C11.508 3.16734 10.6047 3.16601 9.33332 3.16601H6.66665C5.39532 3.16601 4.49265 3.168 3.80665 3.26ZM1.83332 8.00001C1.83332 7.43067 1.83332 6.93534 1.84198 6.50001H14.158C14.1667 6.93534 14.1667 7.43067 14.1667 8.00001V9.33334C14.1667 10.6047 14.1653 11.508 14.0733 12.1933C13.9833 12.8633 13.814 13.25 13.532 13.532C13.25 13.814 12.8633 13.9833 12.1927 14.0733C11.508 14.1653 10.6047 14.1667 9.33332 14.1667H6.66665C5.39532 14.1667 4.49265 14.1653 3.80665 14.0733C3.13665 13.9833 2.74998 13.814 2.46798 13.532C2.18598 13.25 2.01665 12.8633 1.92665 12.1927C1.83465 11.508 1.83332 10.6047 1.83332 9.33334V8.00001Z"
                fill="black"
              />
            </svg>

            <span className="text-xs">
              {new Date().toLocaleDateString("en-US", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </span>
          </div>
        </div>

        {isLoadingClaim ? (
          <div className="flex justify-center items-center py-8">
            <div className="inline-block w-8 h-8 rounded-full border-b-2 border-gray-900 animate-spin"></div>
            <p className="ml-2 text-gray-600">Loading your certificate...</p>
          </div>
        ) : !claim ? (
          <div className="py-8 text-center">
            <p className="mb-4 text-gray-600">
              You haven't claimed your 223 certificate yet.
            </p>

            <button
              onClick={() => navigate("/claim/expodemo")}
              className="px-6 py-2 text-sm font-bold text-white bg-[#1F1F1F] rounded-[50px] hover:bg-gray-800 transition-colors"
            >
              Claim Now
            </button>
          </div>
        ) : (
          <div className="mt-4">
            <div className="relative rounded-[14px] overflow-hidden aspect-square bg-white border border-gray-200 flex items-center justify-center p-6">
              <img
                src="/images/expodemo/cert_of_auth.png"
                alt="223 Certificate Of Authenticity"
                className="object-contain w-full h-full"
              />
            </div>
          </div>
        )}
      </div>

      <div
        className="bg-gradient-to-r from-[#5ACCF1] to-[#1173BB] p-4 rounded-[10px] px-[29.5px] py-3.5 font-bold sm:text-lg text-base text-white cursor-pointer text-center"
        onClick={() =>
          window.open("https://www.223.world/", "_blank", "noopener,noreferrer")
        }
      >
        Visit our 223 Store!
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div
          className="overflow-hidden relative bg-white aspect-square cursor-pointer"
          onClick={() =>
            window.open(
              "https://www.instagram.com/223w0rld/",
              "_blank",
              "noopener,noreferrer",
            )
          }
        >
          <img
            src="/images/expodemo/insta-border.png"
            alt="223 Instagram"
            className="object-contain w-full h-full"
          />

          <div className="flex absolute right-2 left-2 bottom-2 justify-center">
            <button
              className="text-xs bg-[#343434] text-white py-1.5 px-3 rounded-[50px] w-full"
              onClick={(e) => {
                e.stopPropagation();
                window.open(
                  "https://www.instagram.com/223w0rld/",
                  "_blank",
                  "noopener,noreferrer",
                );
              }}
            >
              Follow us on Instagram
            </button>
          </div>
        </div>

        <div
          className="overflow-hidden relative bg-white aspect-square cursor-pointer"
          onClick={() =>
            window.open(
              "https://x.com/223W0RLD",
              "_blank",
              "noopener,noreferrer",
            )
          }
        >
          <img
            src="/images/expodemo/twitter-border.png"
            alt="223 Twitter"
            className="object-contain w-full h-full"
          />

          <div className="flex absolute right-2 left-2 bottom-2 justify-center">
            <button
              className="text-xs bg-[#343434] text-white py-1.5 px-3 rounded-[50px] w-full"
              onClick={(e) => {
                e.stopPropagation();
                window.open(
                  "https://x.com/223W0RLD",
                  "_blank",
                  "noopener,noreferrer",
                );
              }}
            >
              Follow us on Twitter
            </button>
          </div>
        </div>
      </div>

      <div
        className="relative rounded-[15px] overflow-hidden cursor-pointer"
        onClick={() =>
          window.open("https://www.223.world/", "_blank", "noopener,noreferrer")
        }
      >
        <img
          src="/images/expodemo/223_wallpaper.png"
          alt="223 Merging Worlds"
          className="w-full aspect-video max-h-[151px] object-cover"
        />

        <div className="flex absolute right-0 left-0 bottom-4 justify-center">
          <button
            className="text-sm font-bold bg-[#343434] text-white w-[112px] h-[30px] rounded-full"
            onClick={(e) => {
              e.stopPropagation();
              window.open("https://www.223.world/", "_blank", "noopener,noreferrer");
            }}
          >
            Visit
          </button>
        </div>
      </div>

      <div
        className="relative rounded-[15px] overflow-hidden cursor-pointer"
        onClick={() =>
          window.open(
            "https://shop223.world/pages/nobody-sausage",
            "_blank",
            "noopener,noreferrer",
          )
        }
      >
        <img
          src="/images/expodemo/ed_mew_wallpaper.png"
          alt="223 Shop"
          className="w-full aspect-video max-h-[151px]"
        />

        <div className="flex absolute right-0 left-0 bottom-4 justify-center">
          <button
            className="text-sm font-bold bg-[#343434] text-white w-[112px] h-[30px] rounded-full"
            onClick={(e) => {
              e.stopPropagation();
              window.open(
                "https://shop223.world/pages/nobody-sausage",
                "_blank",
                "noopener,noreferrer",
              );
            }}
          >
            Shop
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExpoDemo;
